interface MagneticEl extends HTMLElement {
  _magneticCleanup?: () => void
  _magneticDisposed?: boolean
}

// v-magnetic — pulls the element toward the cursor on mousemove and snaps back
// on leave. Strength can be tuned per-element via `v-magnetic="0.4"` (default 0.3).
// Inline transform from gsap.quickTo overrides Tailwind's hover translate, so the
// existing button hover-lift is replaced by the magnetic follow.
//
// Registered universally (not `.client`) so SSR can resolve the directive — the
// effect itself is client-only because `mounted` never runs on the server, and
// `getSSRProps` is a no-op.
//
// GSAP is imported on first use rather than at the top of this file. The
// directive has to be registered globally, but a static import would put GSAP
// in the main bundle of every page; only landing sections use `v-magnetic`, so
// pages without one never download it.
export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('magnetic', {
    getSSRProps() {
      return {}
    },
    mounted(el: MagneticEl, binding) {
      // Disable on touch devices: no cursor to track
      if (window.matchMedia('(hover: none)').matches) return

      el._magneticDisposed = false

      import('gsap').then(({ gsap }) => {
        // The element may have been removed while GSAP was loading.
        if (el._magneticDisposed) return

        const strength = typeof binding.value === 'number' ? binding.value : 0.3
        const xTo = gsap.quickTo(el, 'x', { duration: 0.45, ease: 'power3.out' })
        const yTo = gsap.quickTo(el, 'y', { duration: 0.45, ease: 'power3.out' })

        const onMove = (e: MouseEvent) => {
          const rect = el.getBoundingClientRect()
          xTo((e.clientX - rect.left - rect.width / 2) * strength)
          yTo((e.clientY - rect.top - rect.height / 2) * strength)
        }
        const onLeave = () => {
          xTo(0)
          yTo(0)
        }

        el.addEventListener('mousemove', onMove)
        el.addEventListener('mouseleave', onLeave)
        el._magneticCleanup = () => {
          el.removeEventListener('mousemove', onMove)
          el.removeEventListener('mouseleave', onLeave)
        }
      })
    },
    unmounted(el: MagneticEl) {
      el._magneticDisposed = true
      el._magneticCleanup?.()
    },
  })
})
